import {
  Action,
  AllActions,
  DeploymentUnitStatus,
  TargetStatus,
} from '@agilelab/plugin-wb-builder-common';
import {
  DefaultCell,
  TableCellProps,
  TableContentLoader,
  WbButton,
  WbSelect,
  WbTable,
  WbTruncatedTypographyWrapper,
} from '@agilelab/plugin-wb-platform';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import TabIcon from '@material-ui/icons/SubdirectoryArrowRight';
import LinkIcon from '@material-ui/icons/Link';
import { snakeCaseToTitleCase } from '@agilelab/plugin-wb-platform-common';
import { Box, makeStyles, Typography, useTheme } from '@material-ui/core';
import { Dispatch, FC, SetStateAction, useCallback } from 'react';
import { useReleaseDetailPageContext } from '../context/useReleaseDetailPageContext';
import { ComponentDependencies } from '../ProvisioningCard/ComponentsTable/ComponentDependencies';
import { DeploymentPreview, PreviewMessage } from '../types';
import { buildChildrenMap, buildParentMap } from '../utils';
import { DeploymentPreviewChip } from './DeploymentPreviewChip';
import { useDeploymentPreviewContext } from './context/useDeploymentPreviewContext';
import { getAllowedActions } from './utils';

const updateParents = (
  newRow: DeploymentPreview,
  rows: DeploymentPreview[],
) => {
  let hasAutoUpdate = false;

  const parentMap = buildParentMap(rows);
  const parentIds = parentMap[newRow.id];
  const newRows = rows.map(row => {
    if (parentIds.includes(row.id) && row.newState !== TargetStatus.DEPLOYED) {
      hasAutoUpdate = true;
      return {
        ...row,
        newState: TargetStatus.DEPLOYED,
        action: Action.DEPLOY,
        autoUpdated: true,
      };
    }
    return { ...row, autoUpdated: false };
  });

  return { rows: newRows, hasAutoUpdate };
};

const updateChildren = (
  newRow: DeploymentPreview,
  rows: DeploymentPreview[],
) => {
  let hasAutoUpdate = false;

  const childrenMap = buildChildrenMap(rows);
  const childrenIds = childrenMap[newRow.id];
  const newRows = rows.map(row => {
    if (
      childrenIds.includes(row.id) &&
      row.newState !== TargetStatus.NOT_DEPLOYED
    ) {
      hasAutoUpdate = true;
      return {
        ...row,
        newState: TargetStatus.NOT_DEPLOYED,
        action: Action.UNDEPLOY,
        autoUpdated: true,
      };
    }
    return { ...row, autoUpdated: false };
  });
  return { rows: newRows, hasAutoUpdate };
};

const updateSubComponents = (
  newRow: DeploymentPreview,
  rows: DeploymentPreview[],
) => {
  let hasAutoUpdate = false;

  const newRows = rows.map(row => {
    if (row.id !== newRow.id) return row;
    const newComponents = row.components?.map(subComponent => {
      if (subComponent.newState !== newRow.newState) {
        hasAutoUpdate = true;
        return {
          ...subComponent,
          newState: newRow.newState,
          action: newRow.action,
          autoUpdated: true,
        };
      }
      return subComponent;
    });
    return { ...row, components: newComponents };
  });

  return { rows: newRows, hasAutoUpdate };
};

const useStyles = makeStyles(theme => ({
  selectInput: {
    height: '36px',
    minHeight: 'auto',
    fontSize: theme.typography.body1.fontSize,
  },
  selectTextField: {
    minHeight: 0,
  },
  flexBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  autoUpdated: {
    background: theme.palette.bkg.secondary,
  },
}));

interface Props {
  setMessage: Dispatch<SetStateAction<PreviewMessage | undefined>>;
  loading: boolean;
}

export const DeploymentPreviewAdvancedTable: FC<Props> = ({
  setMessage,
  loading,
}) => {
  const theme = useTheme();
  const classes = useStyles();

  const { isPartialProvisioningEnabled } = useReleaseDetailPageContext();

  const {
    temporarySelectedItems,
    setTemporarySelectedItems,
    previews,
    setPreviews,
  } = useDeploymentPreviewContext();

  const checkRows = useCallback(
    (newRow: DeploymentPreview, rows: DeploymentPreview[]) => {
      let hasAutoUpdate = false;
      let newRows: DeploymentPreview[] = [...rows];

      if (newRow.newState === TargetStatus.DEPLOYED) {
        const response = updateParents(newRow, rows);
        newRows = response.rows;
        hasAutoUpdate = response.hasAutoUpdate;
      }
      if (newRow.newState === TargetStatus.NOT_DEPLOYED) {
        const response = updateChildren(newRow, rows);
        newRows = response.rows;
        hasAutoUpdate = response.hasAutoUpdate;
      }
      const subComponentsResponse = updateSubComponents(newRow, newRows);
      newRows = subComponentsResponse.rows;
      hasAutoUpdate = hasAutoUpdate || subComponentsResponse.hasAutoUpdate;

      const type: PreviewMessage['type'] = 'info';
      const title = 'Dependencies Highlighted';
      const content = (
        <Typography>
          Due to some dependencies, some previously unselected components will
          also be updated. Those components are highlighted in grey in the table
          below.
        </Typography>
      );
      const message = hasAutoUpdate ? { type, title, content } : undefined;
      setMessage(message);

      return newRows;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleActionChange = (field: DeploymentPreview, newAction: Action) => {
    setPreviews(lastPreviews =>
      lastPreviews.map(preview => {
        if (preview.id !== field.parent) return preview;
        return {
          ...preview,
          components: preview.components?.map(c =>
            c.id === field.id ? { ...c, action: newAction } : c,
          ),
        };
      }),
    );
  };

  const columns: TableCellProps<DeploymentPreview>[] = [
    {
      field: 'name',
      headerName: 'Name',
      resizable: true,
      fieldRender: field => (
        <div className={classes.flexBox}>
          {field.parent && (
            <TabIcon fontSize="small" htmlColor={theme.palette.grey[500]} />
          )}
          {field.autoUpdated && <LinkIcon color="primary" />}
          <WbTruncatedTypographyWrapper
            value={field.name}
            style={{ fontWeight: 700 }}
          />
        </div>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      resizable: true,
      fieldRender: field => <DefaultCell value={field.kind} />,
      formatter: value => snakeCaseToTitleCase(value),
      width: '20%',
    },
    {
      field: 'dependencies',
      headerName: 'Dependencies',
      resizable: true,
      width: '15%',

      fieldRender: field => <ComponentDependencies preview={field} />,
    },
    {
      field: 'currentState',
      headerName: 'Current State',
      resizable: true,
      fieldRender: field => (
        <DeploymentPreviewChip
          status={field.status}
          version={field.statusVersion}
        />
      ),
    },
    {
      field: 'arrow',
      headerName: '',
      uniqueFieldId: 'arrow-decoration',
      resizable: false,
      width: 30,
      fieldRender: () => (
        <ArrowForwardIcon htmlColor={theme.palette.accent.main} />
      ),
    },
    {
      field: 'newState',
      headerName: 'New State',
      resizable: true,
      minWidth: !isPartialProvisioningEnabled ? 80 : 160,
      fieldRender: field => {
        return (
          <div>
            {!isPartialProvisioningEnabled ? (
              <DeploymentPreviewChip
                status={field.newState! as unknown as DeploymentUnitStatus}
              />
            ) : (
              <WbSelect
                disabled={field.parent}
                fullWidth
                ariaLabel="New State"
                value={field?.newState}
                onChange={e => {
                  const value = e.target.value as TargetStatus;
                  const newRows = [...previews];
                  const i = newRows.findIndex(row => row.id === field.id);
                  const row = { ...newRows[i] };
                  row.newState = value;

                  const allowedActions = getAllowedActions(
                    row.status,
                    row.newState,
                  );

                  row.action = allowedActions.includes(field.action!)
                    ? field.action
                    : allowedActions[0];

                  newRows[i] = row;

                  setPreviews(checkRows(row, newRows));
                  setTemporarySelectedItems(prev =>
                    prev.map(item => (item.id === row.id ? row : item)),
                  );
                }}
                options={[TargetStatus.DEPLOYED, TargetStatus.NOT_DEPLOYED]}
                InputProps={{
                  className: classes.selectInput,
                }}
                getOptionLabel={value => snakeCaseToTitleCase(value)}
                className={classes.selectTextField}
                SelectProps={{
                  renderValue: value => (
                    <DeploymentPreviewChip
                      disabled={field.parent}
                      status={value as DeploymentUnitStatus}
                    />
                  ),
                }}
              />
            )}
          </div>
        );
      },
    },
    {
      field: 'action',
      headerName: 'Action',
      resizable: true,
      minWidth: 150,
      fieldRender: field => {
        if (field.parent) {
          return (
            <WbSelect
              fullWidth
              ariaLabel="action"
              value={field.action}
              disabled={
                (getAllowedActions?.(field.status, field.newState!) || [])
                  .length <= 1
              }
              InputProps={{
                className: classes.selectInput,
              }}
              className={classes.selectTextField}
              options={
                getAllowedActions?.(field.status, field.newState!) || AllActions
              }
              onChange={e =>
                handleActionChange(field, e.target.value as Action)
              }
              getOptionLabel={value => snakeCaseToTitleCase(value)}
            />
          );
        }
        return (
          <WbSelect
            fullWidth
            ariaLabel="Action"
            value={field.action}
            className={classes.selectTextField}
            disabled={
              getAllowedActions(field.status, field.newState!).length <= 1
            }
            onChange={e => {
              const value = e.target.value as Action;
              setPreviews(prev =>
                prev.map(p =>
                  p.id === field.id ? { ...p, action: value } : p,
                ),
              );
              setTemporarySelectedItems(prev =>
                prev.map(item =>
                  item.id === field.id ? { ...item, action: value } : item,
                ),
              );
            }}
            options={getAllowedActions(field.status, field.newState!)}
            getOptionLabel={value => snakeCaseToTitleCase(value)}
            InputProps={{
              className: classes.selectInput,
            }}
          />
        );
      },
    },
  ];

  const getRowId = (row: DeploymentPreview) => {
    return row.id;
  };

  const getRowClassName = (row: DeploymentPreview) => {
    if (row.autoUpdated) return classes.autoUpdated;
    return undefined;
  };

  return (
    <>
      {temporarySelectedItems?.length > 0 && (
        <Box className={classes.flexBox}>
          <Typography
            role="status"
            variant="body2"
            style={{ fontWeight: 'medium' }}
          >
            {`${temporarySelectedItems?.length ?? 0} selected`}
          </Typography>
          <WbButton
            variant="text"
            label="Clear"
            onClick={() => setTemporarySelectedItems([])}
          />
        </Box>
      )}

      <WbTable
        selection="multiple"
        selectedItems={temporarySelectedItems}
        setSelectedItems={setTemporarySelectedItems}
        stickyHeader
        components={{
          tableLoader: {
            loading: loading,
            loader: <TableContentLoader rows={10} tableCells={8} />,
          },

          tableContent: {
            columns,
            rows: previews.flatMap(preview => [
              preview,
              ...((preview.components ?? []) as unknown as DeploymentPreview[]),
            ]),
            getRowId,
            rowDynamicClassName: getRowClassName,
          },
        }}
        styles={{
          container: { height: '100%' },
        }}
      />
    </>
  );
};
